// Contrato do Ambiente (docs/22): a tela de login, a área logada (o que o aluno vê além do
// que comprou), os avisos da Operação e os banners do Comercial na área do aluno.
// Sem importar o index: ele importa daqui (a tela de login e a área logada vão na instância).
import * as z from 'zod/mini'

const hex = z.string().check(z.regex(/^#[0-9a-fA-F]{6}$/, 'cor em hexadecimal, como #2f5bea'))

// ---------------------------------------------------------------- tela de login
/**
 * A tela de login da instância: textos, plano de fundo (arquivo), o véu sobre ele (cor e opacidade) e uma imagem
 * opcional sobre o painel. Vazio = o padrão de sempre (o degradê da cor principal e os textos da plataforma).
 */
export const TelaDeLogin = z.object({
  /** a frase grande do painel; vazia = "Seus cursos, no seu ritmo." */
  titulo: z.string(),
  /** o texto abaixo da frase; vazio = o texto padrão */
  texto: z.string(),
  /** a linha pequena do pé do painel; vazia = "Ambiente virtual de aprendizagem" */
  rodape: z.string(),
  /** o título do formulário; vazio = "Entrar" */
  tituloFormulario: z.string(),
  /** o plano de fundo do painel (foto) */
  fundoUrl: z.nullable(z.string()),
  /** o véu sobre o plano de fundo; nula = a cor principal */
  overlayCor: z.nullable(hex),
  /** de 0 (sem véu) a 90 (quase opaco) */
  overlayOpacidade: z.int().check(z.minimum(0), z.maximum(90)),
  /** uma imagem opcional sobre o painel (um selo, uma ilustração, a fachada) */
  imagemUrl: z.nullable(z.string()),
  /** mostrar o logotipo na tela de login (PO, 27/10/2026); desligado, a tela fica só com os textos */
  mostrarLogo: z.boolean(),
})
export type TelaDeLogin = z.infer<typeof TelaDeLogin>
export const LOGIN_PADRAO: TelaDeLogin = {
  titulo: '',
  texto: '',
  rodape: '',
  tituloFormulario: '',
  fundoUrl: null,
  overlayCor: null,
  overlayOpacidade: 55,
  imagemUrl: null,
  mostrarLogo: true,
}
const texto = (max: number) => z.optional(z.string().check(z.maxLength(max)))
export const SalvarTelaDeLogin = z.object({
  titulo: texto(120),
  texto: texto(400),
  rodape: texto(120),
  tituloFormulario: texto(60),
  overlayCor: z.optional(z.nullable(hex)),
  overlayOpacidade: z.optional(z.int().check(z.minimum(0), z.maximum(90))),
  mostrarLogo: z.optional(z.boolean()),
})

// ---------------------------------------------------------------- área logada
export const AreaLogada = z.object({
  /**
   * Mostrar, no Início do aluno, os cursos que ele não comprou (as seções da vitrine). Padrão: sim (infoprodutor).
   * Na IES formal a recomendação é não: o aluno só vê o que cursa.
   */
  mostrarNaoComprados: z.boolean(),
  /** a capa do topo do Início do aluno (como a da vitrine); nula = o degradê da cor */
  capaUrl: z.optional(z.nullable(z.string())),
})
export type AreaLogada = z.infer<typeof AreaLogada>
export const AREA_LOGADA_PADRAO: AreaLogada = { mostrarNaoComprados: true }

/** Um cartão de curso não comprado: leva à página dele na vitrine (ou à da oferta, no combo). */
export const CartaoNaoComprado = z.object({
  id: z.string(),
  titulo: z.string(),
  url: z.string(),
  capa: z.nullable(z.string()),
  etiqueta: z.string(),
  cargaHoraria: z.nullable(z.int()),
  /** o cartão é de uma página de oferta que vende mais de um produto */
  combo: z.optional(z.boolean()),
  /** o preço curto, só nos cartões de oferta */
  preco: z.optional(z.nullable(z.string())),
})
export type CartaoNaoComprado = z.infer<typeof CartaoNaoComprado>
/** O tipo de uma seção da vitrine (Vitrine › Seções). */
export const TipoDeSecao = z.enum(['cursos', 'destaques', 'texto', 'perguntas', 'cartoes'])
export type TipoDeSecao = z.infer<typeof TipoDeSecao>
/**
 * As seções da vitrine marcadas para a área logada. As de cursos e de destaques vêm sem o que o aluno já tem e
 * trazem `cartoes` (`verTodos`: o catálogo, já filtrado quando dá); as de conteúdo trazem `html` (texto, já limpo
 * no servidor) ou `itens` (perguntas e cartões, com `colunas`).
 */
export const VitrineDaArea = z.object({
  secoes: z.array(
    z.object({
      id: z.string(),
      tipo: TipoDeSecao,
      titulo: z.string(),
      subtitulo: z.nullable(z.string()),
      verTodos: z.string(),
      cartoes: z.array(CartaoNaoComprado),
      html: z.string(),
      itens: z.array(z.object({ titulo: z.string(), texto: z.string() })),
      colunas: z.int(),
    }),
  ),
})
export type VitrineDaArea = z.infer<typeof VitrineDaArea>

// ---------------------------------------------------------------- público (avisos e banners)
/** Quem vê: todos os alunos, quem tem um curso, quem está numa turma ou (só banners) quem ainda não tem um curso. */
export const TipoPublico = z.enum(['todos', 'curso', 'turma', 'sem-curso'])
export type TipoPublico = z.infer<typeof TipoPublico>
export const Publico = z.object({ tipo: TipoPublico, alvoId: z.nullable(z.string()) })
export type Publico = z.infer<typeof Publico>
/** O público como o painel mostra: com o nome do curso ou da turma. */
export const PublicoPainel = z.object({
  tipo: TipoPublico,
  alvoId: z.nullable(z.string()),
  alvoNome: z.nullable(z.string()),
})
export type PublicoPainel = z.infer<typeof PublicoPainel>
const data = z.nullable(z.string().check(z.regex(/^\d{4}-\d{2}-\d{2}(T[\d:.]+Z?)?$/, 'data inválida')))

// ---------------------------------------------------------------- avisos (Operação)
export const NIVEIS_AVISO = ['info', 'importante', 'critico'] as const
export const NivelAviso = z.enum(NIVEIS_AVISO)
export type NivelAviso = z.infer<typeof NivelAviso>
export const ROTULO_NIVEL: Record<NivelAviso, string> = {
  info: 'Informativo',
  importante: 'Importante',
  critico: 'Crítico',
}

export const SalvarAviso = z.object({
  titulo: z.string().check(z.trim(), z.minLength(2), z.maxLength(120)),
  texto: z.string().check(z.trim(), z.maxLength(1000)),
  nivel: NivelAviso,
  publico: Publico,
  /** quando começa a aparecer; nulo = agora */
  inicio: data,
  /** quando deixa de aparecer; nulo = até tirar */
  fim: data,
  /** um link opcional ("Saiba mais"): caminho da área do aluno (/aluno/…) ou endereço completo */
  link: z.nullable(z.string().check(z.maxLength(400))),
})
export type SalvarAviso = z.infer<typeof SalvarAviso>
export const AvisoPainel = z.object({
  id: z.string(),
  titulo: z.string(),
  texto: z.string(),
  nivel: NivelAviso,
  publico: PublicoPainel,
  inicio: z.nullable(z.string()),
  fim: z.nullable(z.string()),
  link: z.nullable(z.string()),
  /** agendado, no ar ou encerrado, pelo período */
  situacao: z.enum(['agendado', 'no-ar', 'encerrado']),
  /** quantos alunos dispensaram */
  dispensas: z.int(),
  criadoEm: z.string(),
})
export type AvisoPainel = z.infer<typeof AvisoPainel>
export const AvisoDoAluno = z.object({
  id: z.string(),
  titulo: z.string(),
  texto: z.string(),
  nivel: NivelAviso,
  link: z.nullable(z.string()),
  em: z.string(),
})
export type AvisoDoAluno = z.infer<typeof AvisoDoAluno>

// ---------------------------------------------------------------- banners (Comercial)
/** Os formatos, como as seções da vitrine: mosaico (um grande e dois menores), grande, pequenos lado a lado e único. */
export const LAYOUTS_BANNER = ['mosaico', 'grande', 'pequeno', 'unico'] as const
export const LayoutBanner = z.enum(LAYOUTS_BANNER)
export type LayoutBanner = z.infer<typeof LayoutBanner>
export const ROTULO_LAYOUT: Record<LayoutBanner, { nome: string; texto: string; max: number }> = {
  mosaico: { nome: 'Mosaico', texto: 'Um grande e dois menores ao lado.', max: 3 },
  grande: { nome: 'Grande', texto: 'Faixa larga; com mais de um, passam sozinhos.', max: 6 },
  pequeno: { nome: 'Pequenos', texto: 'Dois ou três lado a lado.', max: 3 },
  unico: { nome: 'Único', texto: 'Uma imagem só, na largura da página.', max: 1 },
}
export const BannerItem = z.object({
  id: z.string(),
  imagemUrl: z.string(),
  /** o texto alternativo da imagem (quem não vê a imagem lê isto) */
  descricao: z.string(),
  /** âncora interna (caminho da área do aluno, /aluno/…) ou endereço completo; vazio = sem link */
  link: z.nullable(z.string()),
})
export type BannerItem = z.infer<typeof BannerItem>
const linkBanner = z.nullable(
  z
    .string()
    .check(
      z.maxLength(400),
      z.regex(
        /^(\/aluno(\/[^\s]*)?|#[A-Za-z0-9_-]+|https?:\/\/\S+)$/,
        'use um caminho da área (/aluno/…) ou https://…',
      ),
    ),
)
export const SalvarBloco = z.object({
  titulo: z.string().check(z.trim(), z.maxLength(120)),
  layout: LayoutBanner,
  itens: z
    .array(
      z.object({
        id: z.string(),
        /** o arquivo enviado (de /comercial/banners/imagem) */
        imagem: z.string().check(z.regex(/^banner-[0-9a-f-]{36}\.(png|jpg|webp)$/)),
        descricao: z.string().check(z.maxLength(200)),
        link: linkBanner,
      }),
    )
    .check(z.minLength(1), z.maxLength(6)),
  publico: Publico,
  inicio: data,
  fim: data,
  ativo: z.boolean(),
})
export type SalvarBloco = z.infer<typeof SalvarBloco>
export const BlocoPainel = z.object({
  id: z.string(),
  titulo: z.string(),
  layout: LayoutBanner,
  itens: z.array(z.extend(BannerItem, { imagem: z.string() })),
  publico: PublicoPainel,
  inicio: z.nullable(z.string()),
  fim: z.nullable(z.string()),
  ativo: z.boolean(),
  ordem: z.int(),
  situacao: z.enum(['agendado', 'no-ar', 'encerrado', 'desligado']),
})
export type BlocoPainel = z.infer<typeof BlocoPainel>
export const BlocoDoAluno = z.object({
  id: z.string(),
  titulo: z.string(),
  layout: LayoutBanner,
  itens: z.array(BannerItem),
})
export type BlocoDoAluno = z.infer<typeof BlocoDoAluno>

/** O dia (AAAA-MM-DD) no horário de Brasília, como instante: o começo dele, ou o começo do dia seguinte. */
const diaBrasilia = (d: string, seguinte = false) => {
  const t = Date.parse(`${d}T03:00:00.000Z`) + (seguinte ? 86_400_000 : 0)
  return new Date(t).toISOString()
}
/** O período em instantes: data sem hora vale o dia inteiro, no horário de Brasília (fim exclusivo). */
export function limitesDoPeriodo(inicio: string | null, fim: string | null) {
  return {
    ini: inicio && inicio.length === 10 ? diaBrasilia(inicio) : inicio,
    ate: fim && fim.length === 10 ? diaBrasilia(fim, true) : fim,
  }
}
/** Situação pelo período: antes do início, agendado; a partir do fim, encerrado. */
export function situacaoDoPeriodo(inicio: string | null, fim: string | null, agora = new Date().toISOString()) {
  const { ini, ate } = limitesDoPeriodo(inicio, fim)
  if (ini && ini > agora) return 'agendado' as const
  if (ate && ate <= agora) return 'encerrado' as const
  return 'no-ar' as const
}

/** O que o seletor de público oferece: os cursos e as turmas (com o curso). */
export const OpcoesDePublico = z.object({
  cursos: z.array(z.object({ id: z.string(), nome: z.string() })),
  turmas: z.array(z.object({ id: z.string(), nome: z.string(), curso: z.string() })),
})
export type OpcoesDePublico = z.infer<typeof OpcoesDePublico>

/** Um passo do "Primeiros passos" do Início do Ambiente: o que a instituição precisa ter para vender de ponta a ponta. */
export const PassoInicial = z.object({
  id: z.enum(['marca', 'recebimento', 'curso', 'oferta', 'vitrine']),
  rotulo: z.string(),
  ok: z.boolean(),
  /** onde se faz, dentro do app */
  para: z.string(),
})
export type PassoInicial = z.infer<typeof PassoInicial>
